import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TourProvider } from "@/contexts/TourContext";
import { PropertyProvider } from "@/contexts/PropertyContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { Toaster } from "sonner";

import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import { TermsPage, PrivacyPage } from "@/pages/LegalPage";
import LegalDownloadsPage from "@/pages/LegalDownloadsPage";
import LegalRenderPage from "@/pages/LegalRenderPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import DuesSchedulePage from "@/pages/admin/DuesSchedulePage";
import EstoppelPage from "@/pages/admin/EstoppelPage";
import WhatsNewPage from "@/pages/admin/WhatsNewPage";
import DelinquencyLettersPage from "@/pages/admin/DelinquencyLettersPage";
import UnitsManagementPage from "@/pages/admin/UnitsManagementPage";
import SpecialAssessmentsPage from "@/pages/admin/SpecialAssessmentsPage";
import StateDisclosurePage from "@/pages/admin/StateDisclosurePage";
import EmailDomainsPage from "@/pages/admin/EmailDomainsPage";
import StripeTestWalkthroughPage from "@/pages/admin/StripeTestWalkthroughPage";
import BoardElectionsPage from "@/pages/admin/BoardElectionsPage";
import PublicEstoppelRequestPage from "@/pages/PublicEstoppelRequestPage";
import ResidentDuesPage from "@/pages/resident/ResidentDuesPage";
import AutopaySuccessPage from "@/pages/AutopaySuccessPage";

import AdminShell from "@/components/AdminShell";
import ResidentShell from "@/components/ResidentShell";
import VendorShell from "@/components/VendorShell";
import BoardShell from "@/components/BoardShell";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { EmailVerifiedBanner } from "@/components/EmailVerifiedBanner";
import { LegalConsentModal } from "@/components/LegalConsentModal";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import CommunitiesPage from "@/pages/admin/CommunitiesPage";
import UsersPage from "@/pages/admin/UsersPage";
import CommitteesPage from "@/pages/admin/CommitteesPage";
import FinancePage from "@/pages/admin/FinancePage";
import InspectionsPage from "@/pages/admin/InspectionsPage";
import InsurancePage from "@/pages/admin/InsurancePage";
import PackagesPage from "@/pages/admin/PackagesPage";
import AccessControlPage from "@/pages/admin/AccessControlPage";
import EmergencyPage from "@/pages/admin/EmergencyPage";
import ParkingPage from "@/pages/admin/ParkingPage";
import NotificationsPage from "@/pages/admin/NotificationsPage";
import DelinquencyPage from "@/pages/admin/DelinquencyPage";
import VendorsPage from "@/pages/admin/VendorsPage";
import DocumentsPage from "@/pages/admin/DocumentsPage";
import ResidentDocumentsPage from "@/pages/resident/ResidentDocumentsPage";
import ApprovalQueuePage from "@/pages/admin/ApprovalQueuePage";
import PetApprovalsPage from "@/pages/admin/PetApprovalsPage";
import IdVerificationsPage from "@/pages/admin/IdVerificationsPage";
import HighValueTxPage from "@/pages/admin/HighValueTxPage";
import BulkImportPage from "@/pages/admin/BulkImportPage";
import PortfolioPage from "@/pages/admin/PortfolioPage";
import VacancyTrackerPage from "@/pages/admin/VacancyTrackerPage";
import BoardTermsPage from "@/pages/admin/BoardTermsPage";
import ForumPage from "@/pages/ForumPage";
import ForumModerationPage from "@/pages/admin/ForumModerationPage";
import KnowledgeBasePage from "@/pages/admin/KnowledgeBasePage";
import InfrastructurePage from "@/pages/admin/InfrastructurePage";
import SettingsPage from "@/pages/admin/SettingsPage";
import BoardArchivePage from "@/pages/admin/BoardArchivePage";
import SmsConsentPage from "@/pages/admin/SmsConsentPage";
import AdminRfpPage from "@/pages/admin/AdminRfpPage";
import CustomerPacketPage from "@/pages/admin/CustomerPacketPage";
import ApiAndAuditPage from "@/pages/admin/ApiAndAuditPage";
import ResidentsPage from "@/pages/admin/ResidentsPage";
import ResidentDetailPage from "@/pages/admin/ResidentDetailPage";
import PaymentsAdminPage from "@/pages/admin/PaymentsAdminPage";
import AmenitiesAdminPage from "@/pages/admin/AmenitiesAdminPage";
import LostFoundAdminPage from "@/pages/admin/LostFoundAdminPage";
import PetRegistryAdminPage from "@/pages/admin/PetRegistryAdminPage";
import EVotingAdminPage from "@/pages/admin/EVotingAdminPage";
import AdminMaintenancePage from "@/pages/admin/AdminMaintenancePage";
import OnCallSchedulePage from "@/pages/admin/OnCallSchedulePage";
import TechShell from "@/components/TechShell";
import TechHomePage from "@/pages/tech/TechHomePage";
import TechTicketPage from "@/pages/tech/TechTicketPage";
import TechPacePage from "@/pages/tech/TechPacePage";
import TechLeaderboardPage from "@/pages/admin/TechLeaderboardPage";
import CamReconciliationPage from "@/pages/admin/CamReconciliationPage";
import AdminViolationsPage from "@/pages/admin/AdminViolationsPage";
import AdminArcPage from "@/pages/admin/AdminArcPage";
import VendorScorecardsPage from "@/pages/admin/VendorScorecardsPage";
import GLPage from "@/pages/admin/GLPage";
import APPage from "@/pages/admin/APPage";
import WebhookRulesPage from "@/pages/admin/WebhookRulesPage";
import BoardMeetingsPage from "@/pages/admin/BoardMeetingsPage";
import VendorCompliancePage from "@/pages/admin/VendorCompliancePage";
import ReserveStudyPage from "@/pages/admin/ReserveStudyPage";
import Tax1099Page from "@/pages/admin/Tax1099Page";
import StatusPage from "@/pages/admin/StatusPage";
import LeasesPage from "@/pages/admin/LeasesPage";
import ForumDigestPage from "@/pages/admin/ForumDigestPage";
import UnitTurnoverPage from "@/pages/admin/UnitTurnoverPage";
import MoveInChecklistPage from "@/pages/admin/MoveInChecklistPage";
import PermissionTemplatesPage from "@/pages/admin/PermissionTemplatesPage";
import CronHealthPage from "@/pages/admin/CronHealthPage";
import ImpersonationLogPage from "@/pages/admin/ImpersonationLogPage";
import GoLivePage from "@/pages/admin/GoLivePage";
import PasswordChangesPage from "@/pages/admin/PasswordChangesPage";
import FirstLoginPage from "@/pages/FirstLoginPage";
import FirstLoginGuard from "@/components/FirstLoginGuard";
import { OnboardingWizardPage, OnboardingBatchesPage, OnboardingBatchDetailPage } from "@/pages/admin/OnboardingWizardPage";
import MoveOutWizardPage from "@/pages/admin/MoveOutWizardPage";
import BankReconPage from "@/pages/admin/BankReconPage";
import OwnerDirectoryPage from "@/pages/admin/OwnerDirectoryPage";
import OwnerShell from "@/components/OwnerShell";
import OwnerDashboard from "@/pages/owner/OwnerDashboard";
import OwnerStatementsPage from "@/pages/owner/OwnerStatementsPage";
import SmsInboxPage from "@/pages/admin/SmsInboxPage";
import TrustAccountsPage from "@/pages/admin/TrustAccountsPage";
import LeasingPipelinePage from "@/pages/admin/LeasingPipelinePage";
import PublicListingDetailPage, { PublicListingsPage } from "@/pages/public/PublicListingsPages";
import PmSchedulePage from "@/pages/admin/PmSchedulePage";
import DelinquencyWaterfallPage from "@/pages/admin/DelinquencyWaterfallPage";
import Tax1099MiscPage from "@/pages/admin/Tax1099MiscPage";
import ChartOfAccountsPage from "@/pages/admin/ChartOfAccountsPage";
import ScreeningPage from "@/pages/admin/ScreeningPage";
import ScreeningConsentPage from "@/pages/public/ScreeningConsentPage";
import VendorOnboardingPage from "@/pages/public/VendorOnboardingPage";
import JournalEntryPage from "@/pages/admin/JournalEntryPage";
import RenterInsurancePage from "@/pages/admin/RenterInsurancePage";
import BoardVaultPage from "@/pages/board/BoardVaultPage";
import BoardWikiPage from "@/pages/board/BoardWikiPage";
import PerksAdminPage from "@/pages/admin/PerksAdminPage";
import ResidentPerksPage from "@/pages/resident/ResidentPerksPage";
import Pending3DSPage from "@/pages/resident/Pending3DSPage";
import PartnerPortalPage from "@/pages/partner/PartnerPortalPage";
import PartnerSignupPage from "@/pages/partner/PartnerSignupPage";
import VendorUploadPage from "@/pages/public/VendorUploadPage";
import VendorMagicLoginPage from "@/pages/public/VendorMagicLoginPage";
import VendorMyCompliancePage from "@/pages/vendor/VendorMyCompliancePage";

import ResidentDashboard from "@/pages/resident/ResidentDashboard";
import MakePaymentPage from "@/pages/resident/MakePaymentPage";
import AccountLedgerPage from "@/pages/resident/AccountLedgerPage";
import MaintenancePage from "@/pages/resident/MaintenancePage";
import PublicVendorsPage from "@/pages/resident/PublicVendorsPage";
import ArcRequestsPage from "@/pages/resident/ArcRequestsPage";
import ArcTrackerPage from "@/pages/resident/ArcTrackerPage";
import ViolationsPage from "@/pages/resident/ViolationsPage";
import AmenitiesPage from "@/pages/resident/AmenitiesPage";
import LostFoundPage from "@/pages/resident/LostFoundPage";
import ResidentParkingPage from "@/pages/resident/ResidentParkingPage";
import PetRegistryPage from "@/pages/resident/PetRegistryPage";
import ResidentBoardMinutesPage from "@/pages/resident/ResidentBoardMinutesPage";
import EVotingPage from "@/pages/resident/EVotingPage";
import ResidentElectionsPage from "@/pages/resident/ResidentElectionsPage";
import MyProfilePage from "@/pages/resident/MyProfilePage";
import NotificationSettingsPage from "@/pages/resident/NotificationSettingsPage";

import AIHubPage from "@/pages/ai/AIHubPage";
import AIAvatarPage from "@/pages/ai/AIAvatarPage";
import AIConciergePage from "@/pages/ai/AIConciergePage";
import MaintenanceAIPage from "@/pages/ai/MaintenanceAIPage";
import PaymentAdvisorPage from "@/pages/ai/PaymentAdvisorPage";
import ArcAIAssistantPage from "@/pages/ai/ArcAIAssistantPage";
import ViolationPreCheckPage from "@/pages/ai/ViolationPreCheckPage";
import VoiceConciergePage from "@/pages/ai/VoiceConciergePage";
import DocumentAssistantPage from "@/pages/ai/DocumentAssistantPage";

import VendorTicketsPage from "@/pages/vendor/VendorTicketsPage";
import VendorTicketDetailPage from "@/pages/vendor/VendorTicketDetailPage";
import VendorHomePage from "@/pages/vendor/VendorHomePage";
import DisputeWizardPage from "@/pages/admin/DisputeWizardPage";
import VendorNegotiatorPage from "@/pages/admin/VendorNegotiatorPage";
import RealEstatePredictorPage from "@/pages/admin/RealEstatePredictorPage";
import FinancialForecastPage from "@/pages/admin/FinancialForecastPage";
import SentimentPulsePage from "@/pages/admin/SentimentPulsePage";
import PromptLibraryPage from "@/pages/admin/PromptLibraryPage";
import AICostDashboardPage from "@/pages/admin/AICostDashboardPage";
import VendorProfilePage from "@/pages/vendor/VendorProfilePage";
import VendorRfpPage from "@/pages/vendor/VendorRfpPage";
import VendorNotificationsPage from "@/pages/vendor/VendorNotificationsPage";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-sm text-muted-foreground tracking-widest uppercase">Loading…</div>
    </div>
  );
}

function roleHomePath(role) {
  if (role === "admin") return "/admin/dashboard";
  if (role === "property_manager") return "/admin/dashboard";
  if (role === "maintenance") return "/tech";
  if (role === "board") return "/board/finance";
  if (role === "vendor") return "/vendor/home";
  if (role === "partner") return "/partner";
  if (role === "owner") return "/owner/dashboard";
  return "/resident/dashboard";
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={roleHomePath(user.role)} replace />;
}

function RequireAuth({ role, children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  const allowed = Array.isArray(role) ? role : [role];
  if (role && !allowed.includes(user.role)) {
    return <Navigate to={roleHomePath(user.role)} replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <PropertyProvider>
        <RealtimeProvider>
          <TourProvider>
          <BrowserRouter>
          <ImpersonationBanner />
          <EmailVerifiedBanner />
          <LegalConsentModal />
          <FirstLoginGuard />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/partner/signup" element={<PartnerSignupPage />} />
            <Route path="/partner"
                   element={<RequireAuth role={["partner"]}><PartnerPortalPage /></RequireAuth>} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/estoppel-request/:propertyId" element={<PublicEstoppelRequestPage />} />
            <Route path="/first-login" element={<FirstLoginPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/legal/downloads" element={<LegalDownloadsPage />} />
            <Route path="/legal/:slug" element={<LegalRenderPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/payment/autopay-success" element={<AutopaySuccessPage />} />
            <Route path="/vendor-upload/:token" element={<VendorUploadPage />} />
            <Route path="/vendor-login-magic" element={<VendorMagicLoginPage />} />
            <Route path="/vendor-login-magic/:token" element={<VendorMagicLoginPage />} />
            <Route path="/listings" element={<PublicListingsPage />} />
            <Route path="/listings/:lid" element={<PublicListingDetailPage />} />
            <Route path="/screening/:token" element={<ScreeningConsentPage />} />
            <Route path="/vendor-onboarding/:token" element={<VendorOnboardingPage />} />

            {/* R119 — Mobile Tech View (maintenance role gets a dedicated mobile shell) */}
            <Route element={<RequireAuth role={["maintenance", "admin", "property_manager"]}><TechShell /></RequireAuth>}>
              <Route path="/tech" element={<TechHomePage />} />
              <Route path="/tech/ticket/:id" element={<TechTicketPage />} />
              <Route path="/tech/pace" element={<TechPacePage />} />
            </Route>

            <Route element={<RequireAuth role={["admin", "property_manager", "maintenance"]}><AdminShell /></RequireAuth>}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/tech-leaderboard" element={<TechLeaderboardPage />} />
              <Route path="/admin/cam" element={<CamReconciliationPage />} />
              <Route path="/admin/communities" element={<CommunitiesPage />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/committees" element={<CommitteesPage />} />
              <Route path="/admin/finance" element={<FinancePage />} />
              <Route path="/admin/dues" element={<DuesSchedulePage />} />
              <Route path="/admin/gl" element={<GLPage />} />
              <Route path="/admin/ap" element={<APPage />} />
              <Route path="/admin/webhooks" element={<WebhookRulesPage />} />
              <Route path="/admin/inspections" element={<InspectionsPage />} />
              <Route path="/admin/insurance" element={<InsurancePage />} />
              <Route path="/admin/packages" element={<PackagesPage />} />
              <Route path="/admin/access" element={<AccessControlPage />} />
              <Route path="/admin/emergency" element={<EmergencyPage />} />
              <Route path="/admin/parking" element={<ParkingPage />} />
              <Route path="/admin/notifications" element={<NotificationsPage />} />
              <Route path="/admin/delinquency" element={<DelinquencyPage />} />
              <Route path="/admin/vendors" element={<VendorsPage />} />
              <Route path="/admin/documents" element={<DocumentsPage />} />
              <Route path="/admin/maintenance" element={<AdminMaintenancePage />} />
              <Route path="/admin/oncall" element={<OnCallSchedulePage />} />
              <Route path="/admin/violations" element={<AdminViolationsPage />} />
              <Route path="/admin/arc" element={<AdminArcPage />} />

              <Route path="/admin/ai/hub" element={<AIHubPage />} />
              <Route path="/admin/ai" element={<AIHubPage />} />
              <Route path="/admin/ai/avatar" element={<AIAvatarPage />} />
              <Route path="/admin/ai/concierge" element={<AIConciergePage />} />
              <Route path="/admin/ai/voice" element={<VoiceConciergePage />} />
              <Route path="/admin/ai/maintenance" element={<MaintenanceAIPage />} />
              <Route path="/admin/ai/payment-advisor" element={<PaymentAdvisorPage />} />
              <Route path="/admin/ai/arc" element={<ArcAIAssistantPage />} />
              <Route path="/admin/ai/violation" element={<ViolationPreCheckPage />} />
              <Route path="/admin/ai/documents" element={<DocumentAssistantPage />} />

              <Route path="/admin/approvals" element={<ApprovalQueuePage />} />
              <Route path="/admin/pet-approvals" element={<PetApprovalsPage />} />
              <Route path="/admin/id-verifications" element={<IdVerificationsPage />} />
              <Route path="/admin/high-value" element={<HighValueTxPage />} />
              <Route path="/admin/bulk-import" element={<BulkImportPage />} />
              <Route path="/admin/portfolio" element={<PortfolioPage />} />
              <Route path="/admin/vacancy" element={<VacancyTrackerPage />} />
              <Route path="/admin/board-terms" element={<BoardTermsPage />} />
              <Route path="/admin/forum-moderation" element={<ForumModerationPage />} />
              <Route path="/admin/knowledge-base" element={<KnowledgeBasePage />} />
              <Route path="/admin/infrastructure" element={<InfrastructurePage />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/admin/board-archive" element={<BoardArchivePage />} />
              <Route path="/admin/sms-consent" element={<SmsConsentPage />} />
              <Route path="/admin/rfp" element={<AdminRfpPage />} />
              <Route path="/admin/customer-envelopes" element={<CustomerPacketPage />} />
              <Route path="/admin/api" element={<ApiAndAuditPage />} />
              <Route path="/admin/residents" element={<ResidentsPage />} />
              <Route path="/admin/residents/:id" element={<ResidentDetailPage />} />
              <Route path="/admin/payments" element={<PaymentsAdminPage />} />
              <Route path="/admin/amenities" element={<AmenitiesAdminPage />} />
              <Route path="/admin/lost-found" element={<LostFoundAdminPage />} />
              <Route path="/admin/pets" element={<PetRegistryAdminPage />} />
              <Route path="/admin/voting" element={<EVotingAdminPage />} />
              <Route path="/admin/vendor-scorecards" element={<VendorScorecardsPage />} />
              <Route path="/admin/ai/disputes" element={<DisputeWizardPage />} />
              <Route path="/admin/ai/vendor-negotiator" element={<VendorNegotiatorPage />} />
              <Route path="/admin/ai/real-estate" element={<RealEstatePredictorPage />} />
              <Route path="/admin/ai/forecast" element={<FinancialForecastPage />} />
              <Route path="/admin/ai/sentiment" element={<SentimentPulsePage />} />
              <Route path="/admin/ai/prompts" element={<PromptLibraryPage />} />
              <Route path="/admin/ai/usage" element={<AICostDashboardPage />} />
              <Route path="/admin/perks" element={<PerksAdminPage />} />
              <Route path="/admin/board-meetings" element={<BoardMeetingsPage />} />
              <Route path="/admin/vendor-compliance" element={<VendorCompliancePage />} />
              <Route path="/admin/reserve-study" element={<ReserveStudyPage />} />
              <Route path="/admin/estoppel" element={<EstoppelPage />} />
              <Route path="/admin/delinquency-letters" element={<DelinquencyLettersPage />} />
              <Route path="/admin/units-management" element={<UnitsManagementPage />} />
              <Route path="/admin/special-assessments" element={<SpecialAssessmentsPage />} />
              <Route path="/admin/state-disclosure" element={<StateDisclosurePage />} />
              <Route path="/admin/email-domains" element={<EmailDomainsPage />} />
              <Route path="/admin/stripe-walkthrough" element={<StripeTestWalkthroughPage />} />
              <Route path="/admin/board-elections" element={<BoardElectionsPage />} />
              <Route path="/admin/whats-new" element={<WhatsNewPage />} />
              <Route path="/admin/tax/1099" element={<Tax1099Page />} />
              <Route path="/admin/status" element={<StatusPage />} />
              <Route path="/admin/leases" element={<LeasesPage />} />
              <Route path="/admin/unit-turnover" element={<UnitTurnoverPage />} />
              <Route path="/admin/move-in-checklist" element={<MoveInChecklistPage />} />
              <Route path="/admin/permission-templates" element={<PermissionTemplatesPage />} />
              <Route path="/admin/system/crons" element={<CronHealthPage />} />
              <Route path="/admin/system/impersonation-log" element={<ImpersonationLogPage />} />
              <Route path="/admin/system/password-changes" element={<PasswordChangesPage />} />
              <Route path="/admin/onboarding" element={<OnboardingWizardPage />} />
              <Route path="/admin/onboarding/batches" element={<OnboardingBatchesPage />} />
              <Route path="/admin/onboarding/batch/:bid" element={<OnboardingBatchDetailPage />} />
              <Route path="/admin/go-live" element={<GoLivePage />} />
              <Route path="/admin/move-out-wizard" element={<MoveOutWizardPage />} />
              <Route path="/admin/bank-recon" element={<BankReconPage />} />
              <Route path="/admin/owners" element={<OwnerDirectoryPage />} />
              <Route path="/admin/sms-inbox" element={<SmsInboxPage />} />
              <Route path="/admin/trust-accounts" element={<TrustAccountsPage />} />
              <Route path="/admin/leasing" element={<LeasingPipelinePage />} />
              <Route path="/admin/pm-schedule" element={<PmSchedulePage />} />
              <Route path="/admin/delinquency-waterfall" element={<DelinquencyWaterfallPage />} />
              <Route path="/admin/tax/1099-misc" element={<Tax1099MiscPage />} />
              <Route path="/admin/coa" element={<ChartOfAccountsPage />} />
              <Route path="/admin/journal-entries" element={<JournalEntryPage />} />
              <Route path="/admin/screening" element={<ScreeningPage />} />
              <Route path="/admin/renter-insurance" element={<RenterInsurancePage />} />
              <Route path="/admin/forum-digest" element={<ForumDigestPage />} />
            </Route>

            <Route element={<RequireAuth role="resident"><ResidentShell /></RequireAuth>}>
              <Route path="/resident/dashboard" element={<ResidentDashboard />} />
              <Route path="/resident/pay" element={<MakePaymentPage />} />
              <Route path="/resident/dues" element={<ResidentDuesPage />} />
              <Route path="/resident/ledger" element={<AccountLedgerPage />} />
              <Route path="/resident/maintenance" element={<MaintenancePage />} />
              <Route path="/resident/vendors" element={<PublicVendorsPage />} />
              <Route path="/resident/arc" element={<ArcRequestsPage />} />
              <Route path="/resident/arc-tracker" element={<ArcTrackerPage />} />
              <Route path="/resident/violations" element={<ViolationsPage />} />
              <Route path="/resident/amenities" element={<AmenitiesPage />} />
              <Route path="/resident/lost-found" element={<LostFoundPage />} />
              <Route path="/resident/parking" element={<ResidentParkingPage />} />
              <Route path="/resident/pets" element={<PetRegistryPage />} />
              <Route path="/resident/voting" element={<EVotingPage />} />
              <Route path="/resident/elections" element={<ResidentElectionsPage />} />
              <Route path="/resident/board-minutes" element={<ResidentBoardMinutesPage />} />
              <Route path="/resident/profile" element={<MyProfilePage />} />
              <Route path="/resident/notifications" element={<NotificationSettingsPage />} />
              <Route path="/resident/perks" element={<ResidentPerksPage />} />
              <Route path="/resident/forum" element={<ForumPage />} />
              <Route path="/resident/pending-3ds" element={<Pending3DSPage />} />

              <Route path="/resident/ai" element={<AIHubPage />} />
              <Route path="/resident/ai/hub" element={<AIHubPage />} />
              <Route path="/resident/ai/avatar" element={<AIAvatarPage />} />
              <Route path="/resident/ai/concierge" element={<AIConciergePage />} />
              <Route path="/resident/ai/voice" element={<VoiceConciergePage />} />
              <Route path="/resident/ai/maintenance" element={<MaintenanceAIPage />} />
              <Route path="/resident/ai/payment-advisor" element={<PaymentAdvisorPage />} />
              <Route path="/resident/ai/arc" element={<ArcAIAssistantPage />} />
              <Route path="/resident/ai/violation" element={<ViolationPreCheckPage />} />
              <Route path="/resident/ai/documents" element={<DocumentAssistantPage />} />
              <Route path="/resident/documents" element={<ResidentDocumentsPage />} />
            </Route>

            <Route element={<RequireAuth role="vendor"><VendorShell /></RequireAuth>}>
              <Route path="/vendor" element={<VendorHomePage />} />
              <Route path="/vendor/home" element={<VendorHomePage />} />
              <Route path="/vendor/tickets" element={<VendorTicketsPage />} />
              <Route path="/vendor/tickets/:id" element={<VendorTicketDetailPage />} />
              <Route path="/vendor/rfp" element={<VendorRfpPage />} />
              <Route path="/vendor/notifications" element={<VendorNotificationsPage />} />
              <Route path="/vendor/profile" element={<VendorProfilePage />} />
              <Route path="/vendor/my-compliance" element={<VendorMyCompliancePage />} />
            </Route>

            <Route element={<RequireAuth role={["admin", "board"]}><BoardShell /></RequireAuth>}>
              <Route path="/board/finance" element={<FinancePage />} />
              <Route path="/board/vault" element={<BoardVaultPage />} />
              <Route path="/board/wiki" element={<BoardWikiPage />} />
            </Route>

            <Route element={<RequireAuth role="owner"><OwnerShell /></RequireAuth>}>
              <Route path="/owner/dashboard" element={<OwnerDashboard />} />
              <Route path="/owner/statements" element={<OwnerStatementsPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" duration={2500} />
          </BrowserRouter>
          </TourProvider>
        </RealtimeProvider>
      </PropertyProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
